import { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

const DEVELOPMENT_SESSION_KEY = "live-room-development-session";

export type AuthUser = {
  name: string;
  email: string;
  picture?: string;
  provider: string;
};
export type AuthSession = {
  user: AuthUser | null;
  authMode: "google" | "mock" | "";
  clientId: string;
};

export function developmentSessionToken() {
  return sessionStorage.getItem(DEVELOPMENT_SESSION_KEY) || "";
}

function sessionHeaders(): Record<string, string> {
  const token = developmentSessionToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function readJson(response: Response) {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.error || "authentication-failed");
  return body;
}

const sessionKey = ["auth", "session"] as const;

async function loadSession(): Promise<AuthSession> {
  const [sessionResponse, configResponse] = await Promise.all([
    fetch("/auth/me", {
      credentials: "same-origin",
      headers: sessionHeaders(),
    }),
    fetch("/auth/config", { credentials: "same-origin" }),
  ]);
  const config = await readJson(configResponse);
  const session = sessionResponse.ok ? await sessionResponse.json() : null;
  return {
    user: session?.user || null,
    authMode: config.authMode,
    clientId: config.clientId,
  };
}

// The signed-in user plus the server's sign-in configuration.
export function useAuth() {
  const session = useQuery({
    queryKey: sessionKey,
    queryFn: loadSession,
    staleTime: Infinity,
  });
  const signOut = useCallback(async () => {
    await fetch("/auth/logout", {
      method: "POST",
      credentials: "same-origin",
      headers: sessionHeaders(),
    });
    sessionStorage.removeItem(DEVELOPMENT_SESSION_KEY);
    window.location.reload();
  }, []);
  return {
    user: session.data?.user ?? null,
    authMode: session.data?.authMode ?? "",
    clientId: session.data?.clientId ?? "",
    loading: session.isPending,
    error: session.error?.message ?? "",
    signOut,
  };
}

// Google and development sign-in. Success stores the user in the session
// query, which signs the whole app in.
export function useSignIn() {
  const client = useQueryClient();
  const [googleError, setGoogleError] = useState("");
  const signedIn = (user: AuthUser) =>
    client.setQueryData<AuthSession>(sessionKey, (session) =>
      session ? { ...session, user } : session,
    );
  const google = useMutation({
    mutationFn: async (credential: string) =>
      readJson(
        await fetch("/auth/google", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ credential }),
        }),
      ),
    onSuccess: (result) => signedIn(result.user),
  });
  const development = useMutation({
    mutationFn: async (profile: { name: string; email: string }) =>
      readJson(
        await fetch("/auth/development", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(profile),
        }),
      ),
    onSuccess: (result) => {
      sessionStorage.setItem(DEVELOPMENT_SESSION_KEY, result.sessionToken);
      signedIn(result.user);
    },
  });
  const signingIn = google.isPending || development.isPending;
  const mutationError = (google.error || development.error)?.message;
  return {
    signIn: google.mutate,
    signInDevelopment: development.mutate,
    signingIn,
    error: signingIn ? "" : mutationError || googleError,
    setGoogleError,
    resetError: () => {
      google.reset();
      development.reset();
      setGoogleError("");
    },
  };
}
